import { fetchWrapper } from "@/helpers/fetch-wrapper";

/**
 * Ile sekund czekać po operacji sterowania, zanim odpytamy znaki o nowy stan.
 *
 * Sterowniki tablic potrzebują chwili na aktualizację swojego stanu - odpytane za wcześnie zwrócą starą treść.
 * Do 2026-09-24 odczekiwał backend (`sleep()` w kontrolerze), blokując przy tym workera PHP-FPM. Teraz czeka
 * przeglądarka, a serwer odpowiada od razu. Wartość musi odpowiadać `APP_VMS_GET_STATUS_DELAY` po stronie backendu.
 */
export const VMS_STATUS_REFRESH_DELAY_MS =
  (Number(process.env.VUE_APP_VMS_GET_STATUS_DELAY) || 2) * 1000;

/**
 * Serwis do obsługi VMS (tablice zmiennej treści, Variable Message Signs)
 */
class Vms {
  /**
   * Pobiera status wszystkich grup VMS z API.
   *
   * Zwraca trzy mapy indeksowane identyfikatorem grupy: znaki, opisy grup i stacje, do których grupy należą.
   * Stacja jest potrzebna panelowi filtrów - pozwala zawęzić listę do jednej lokalizacji.
   *
   * @param {Object} options
   * @param {Boolean} options.fresh TRUE odpytuje znaki na żywo zamiast czytać ostatni znany stan.
   */
  async getAllGroupStatus({ fresh = false } = {}) {
    try {
      // Wywołanie endpointa z informacjami o grupach VMS
      const response = await fetchWrapper.get(
        `${process.env.VUE_APP_API_URL}/vms-groups-status${fresh ? "?fresh=1" : ""}`
      );

      // Słownik z przypisaniem: id grupy -> tablice VMS w tej grupie
      const groupsStatus = {};
      // Słownik z przypisaniem: id grupy -> opis grupy
      const groupsDescription = {};
      // Słownik z przypisaniem: id grupy -> stacja preselekcji, do której grupa należy
      const groupsLocation = {};

      response.forEach(group => {
        groupsDescription[group.group.id] = group.group.description;
        groupsStatus[group.group.id] = group.vms;
        groupsLocation[group.group.id] = group.group.location || null;
      });

      // Zwraca tablicę [statusy, opisy, stacje]
      return [groupsStatus, groupsDescription, groupsLocation];
    } catch (error) {
      console.error('Błąd podczas pobierania danych:', error);
      return {};
    }
  }

  /**
   * Pobiera status znaków z jednej grupy.
   *
   * Używane do odświeżenia widoku po operacji sterowania - jest wielokrotnie tańsze niż pobranie stanu wszystkich
   * grup w systemie, bo dotyka tylko tych tablic, których operacja dotyczyła.
   *
   * @param {Number} groupId ID grupy VMS
   * @param {Object} options
   * @param {Boolean} options.fresh TRUE odpytuje znaki na żywo zamiast czytać ostatni znany stan.
   *
   * @returns {Promise<Object>} Obiekt grupy w postaci { group, vms }.
   */
  getGroupStatus(groupId, { fresh = false } = {}) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/vms-groups-status/${groupId}${fresh ? "?fresh=1" : ""}`
    );
  }

  /**
   * Pobiera obrazek (np. podgląd znaku VMS) po id jako blob, konwertuje na base64.
   * Zwraca base64 (do <img :src="...">).
   */
  async getImageForSign(id) {
    return fetchWrapper.getBlob(`${process.env.VUE_APP_API_URL}/vms-contents/${id}/image`)
      .then(fetched => {
        return this.blobToBase64(fetched);
      })
      .catch(error =>{
        console.error(error);
      });
  }

  /**
   * Konwertuje obiekt Blob do formatu base64 (do wyświetlania obrazka).
   */
  blobToBase64(blob) {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise(resolve => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  }

  /**
   * Pobiera dostępne treści (warianty komunikatów) dla tablic VMS.
   */
  getContents() {
    return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/vms-contents`);
  }

  /**
   * Włącza wybrany wariant komunikatu na grupie VMS.
   *
   * Backend zwraca 204 (bez ciała), gdy komunikat przyjęły WSZYSTKIE znaki w grupie. Gdy którykolwiek zawiódł,
   * odpowiada 200 z opisem tego, co się wydarzyło - patrz `hasFailures()`. Gdy choć jeden znak jest niedostępny
   * jeszcze przed rozpoczęciem operacji, odrzuca żądanie kodem 409 (obowiązuje zasada "albo cała grupa, albo nic").
   *
   * @param {Number} groupId - ID grupy VMS
   * @param {Number} variant - numer wariantu (np. treść komunikatu)
   *
   * @returns {Promise<Object|null>} NULL oznacza pełny sukces.
   */
  enableVms(groupId, variant) {
    return fetchWrapper.put(`${process.env.VUE_APP_API_URL}/vms-groups-status/${groupId}/variant/${variant}`);
  }

  /**
   * Wyłącza komunikat na grupie VMS.
   *
   * Gaszenie prowadzi w stronę stanu bezpieczniejszego, więc jest wykonywane na wszystkich osiągalnych znakach -
   * niedostępność jednej tablicy nie blokuje wygaszenia pozostałych. Znaki, do których nie udało się dotrzeć,
   * backend wymienia w odpowiedzi.
   *
   * @param {Number} groupId - ID grupy
   *
   * @returns {Promise<Object|null>} NULL oznacza pełny sukces.
   */
  disableVms(groupId) {
    return fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/vms-groups-status/${groupId}/variant`);
  }

  /**
   * Pobiera ostatnią operację sterowania wykonaną na znakach z grupy.
   *
   * @param {Number} groupId - ID grupy
   *
   * @returns {Promise<Object|null>} NULL, gdy grupą jeszcze nie sterowano.
   */
  getGroupLastLog(groupId) {
    return fetchWrapper
      .get(`${process.env.VUE_APP_API_URL}/vms-groups/${groupId}/last-log`)
      .catch(error => {
        // Brak historii nie jest błędem, o którym warto zawracać głowę operatorowi - nagłówek grupy po prostu
        // nie pokaże informacji o ostatniej operacji.
        console.error('Błąd podczas pobierania ostatniej operacji na grupie:', error);
        return null;
      });
  }

  /**
   * Określa czy odpowiedź operacji sterowania opisuje niepowodzenie na którymkolwiek ze znaków.
   *
   * @param {Object|null} operationResponse Odpowiedź zwrócona przez `enableVms()` / `disableVms()`.
   *
   * @returns {Boolean}
   */
  hasFailures(operationResponse) {
    if (!operationResponse) {
      return false;
    }

    return Boolean(
      operationResponse.failed?.length ||
      operationResponse.rolledBack ||
      operationResponse.rollbackFailed?.length
    );
  }

  /**
   * Skleja listę znaków z odpowiedzi operacji w czytelny opis, np. "A4/126+300, A4/128+100".
   *
   * @param {Array} results Tablica obiektów z wynikiem operacji dla pojedynczych znaków.
   *
   * @returns {String}
   */
  formatSignList(results = []) {
    return results.map(result => `${result.roadNumber}/${result.chainage}`).join(', ');
  }
}

// Eksport singletonu klasy
export default new Vms();
